import React, { useState, useRef } from "react";
import { colors } from "../../constants/theme";
import { Button } from "../ui/Button";

interface UploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
}

export const UploadDialog: React.FC<UploadDialogProps> = ({
  isOpen,
  onClose,
  onUpload,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    
    // Close dialog immediately when upload starts
    // The notification will show the upload progress/status
    const fileToUpload = selectedFile;
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
    
    // Start upload (notification will be handled by parent)
    try {
      await onUpload(fileToUpload);
    } catch (error) {
      console.error("Upload failed:", error);
      // Error is handled by parent component via notification
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onClose();
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: colors.card,
          borderRadius: 12,
          padding: 24,
          minWidth: 400,
          maxWidth: 500,
          boxShadow: colors.shadow,
          border: `1px solid ${colors.border}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: "0 0 20px 0", fontSize: 20, color: colors.text }}>
          Upload File
        </h2>

        <div style={{ marginBottom: 20 }}>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            disabled={isUploading}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              fontSize: 14,
              cursor: isUploading ? "not-allowed" : "pointer",
            }}
          />
        </div>

        {selectedFile && (
          <div
            style={{
              marginBottom: 20,
              padding: 12,
              background: colors.bg,
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div style={{ fontSize: 14, color: colors.text, fontWeight: 600 }}>
              Selected: {selectedFile.name}
            </div>
            <div
              style={{ fontSize: 12, color: colors.muted, marginTop: 4 }}
            >
              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button
            onClick={handleClose}
            disabled={isUploading}
            kind="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            kind="primary"
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </div>
      </div>
    </div>
  );
};

